"use client";

import { format, isValid, parse } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DatePickerInputProps {
  value?: Date | string | null;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  id?: string;
  name?: string;
  required?: boolean;
  onValidationChange?: (isValid: boolean) => void;
}

/**
 * Converts a value to a Date object
 */
function toDate(value: Date | string | null | undefined): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return isValid(value) ? value : undefined;

  // Try to parse as ISO date (YYYY-MM-DD)
  if (typeof value === "string") {
    // Handle ISO format
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const parsed = parse(value, "yyyy-MM-dd", new Date());
      return isValid(parsed) ? parsed : undefined;
    }

    // Try to parse the string as a date
    const parsed = new Date(value);
    return isValid(parsed) ? parsed : undefined;
  }

  return undefined;
}

export function DatePickerInput({
  value,
  onChange,
  disabled = false,
  className,
  minDate,
  maxDate,
  id,
  name,
  required = false,
  onValidationChange,
}: DatePickerInputProps) {
  const [open, setOpen] = React.useState(false);
  const [year, setYear] = React.useState("");
  const [month, setMonth] = React.useState("");
  const [day, setDay] = React.useState("");
  const [focusedSegment, setFocusedSegment] = React.useState<
    "year" | "month" | "day" | null
  >(null);
  const [isValidInput, setIsValidInput] = React.useState(true);

  const yearRef = React.useRef<HTMLInputElement>(null);
  const monthRef = React.useRef<HTMLInputElement>(null);
  const dayRef = React.useRef<HTMLInputElement>(null);

  const date = React.useMemo(() => toDate(value), [value]);

  // Memoize calendar navigation bounds to support future dates in dropdown
  const calendarEndMonth = React.useMemo(
    () => maxDate || new Date(new Date().getFullYear() + 5, 11),
    [maxDate],
  );

  // Sync segments with date prop
  React.useEffect(() => {
    if (date && isValid(date)) {
      setYear(format(date, "yyyy"));
      setMonth(format(date, "MM"));
      setDay(format(date, "dd"));
    } else if (!value) {
      setYear("");
      setMonth("");
      setDay("");
    }
  }, [date, value]);

  // Validate and update the date when segments change
  const updateDate = React.useCallback(
    (y: string, m: string, d: string) => {
      if (y.length === 4 && m.length === 2 && d.length === 2) {
        const dateString = `${y}-${m}-${d}`;
        const parsed = parse(dateString, "yyyy-MM-dd", new Date());

        if (isValid(parsed)) {
          // Check date constraints
          if (minDate && parsed < minDate) {
            setIsValidInput(false);
            onValidationChange?.(false);
            return;
          }
          if (maxDate && parsed > maxDate) {
            setIsValidInput(false);
            onValidationChange?.(false);
            return;
          }

          setIsValidInput(true);
          onValidationChange?.(true);
          onChange?.(parsed);
        } else {
          setIsValidInput(false);
          onValidationChange?.(false);
        }
      } else if (y === "" && m === "" && d === "") {
        setIsValidInput(!required);
        onValidationChange?.(!required);
        onChange?.(undefined);
      } else {
        // Partial entry - show as invalid
        setIsValidInput(false);
        onValidationChange?.(false);
      }
    },
    [minDate, maxDate, onChange, onValidationChange, required],
  );

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setYear(value);

    if (value.length === 4) {
      // Auto-advance to month
      monthRef.current?.focus();
    }

    updateDate(value, month, day);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 2);

    // Only auto-format if it's a single digit that can't be part of a valid two-digit month
    // Don't add leading zero for 1 (could be 10, 11, 12)
    // Only add for single digits > 1 when followed by auto-advance
    if (value.length === 1 && parseInt(value, 10) > 1) {
      // Check if this could be part of a valid month (10-12 for "1", 20-29 not valid for months)
      // For months, only 2-9 should get auto-formatted since 20-99 are invalid months
      const formatted = `0${value}`;
      setMonth(formatted);
      // Auto-advance to day for single digit months > 1
      dayRef.current?.focus();
      updateDate(year, formatted, day);
      return;
    }

    setMonth(value);

    if (value.length === 2) {
      // Auto-advance to day
      dayRef.current?.focus();
    }

    updateDate(year, value, day);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 2);

    // Only auto-format single digits that can't be part of a valid two-digit day
    // Days 1-3 could be 10-31, so don't auto-format those
    // Days 4-9 can only be 04-09, so auto-format and complete
    if (value.length === 1 && parseInt(value, 10) > 3) {
      const formatted = `0${value}`;
      setDay(formatted);
      updateDate(year, month, formatted);
      return;
    }

    setDay(value);
    updateDate(year, month, value);
  };

  const handleBlur = () => {
    setFocusedSegment(null);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    segment: "year" | "month" | "day",
  ) => {
    // Handle navigation between segments
    if (e.key === "ArrowRight" || e.key === "/" || e.key === "-") {
      e.preventDefault();
      if (segment === "year") monthRef.current?.focus();
      else if (segment === "month") dayRef.current?.focus();
    } else if (e.key === "ArrowLeft") {
      if (segment === "day") monthRef.current?.focus();
      else if (segment === "month") yearRef.current?.focus();
    } else if (e.key === "Backspace") {
      const target = e.target as HTMLInputElement;
      if (target.value === "" && segment !== "year") {
        e.preventDefault();
        if (segment === "month") {
          yearRef.current?.focus();
          const newYear = year.slice(0, -1);
          setYear(newYear);
          updateDate(newYear, month, day);
        } else if (segment === "day") {
          monthRef.current?.focus();
          const newMonth = month.slice(0, -1);
          setMonth(newMonth);
          updateDate(year, newMonth, day);
        }
      }
    }
  };

  // Handle calendar selection
  const handleCalendarSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      setYear(format(selectedDate, "yyyy"));
      setMonth(format(selectedDate, "MM"));
      setDay(format(selectedDate, "dd"));
      setIsValidInput(true);
      onValidationChange?.(true);
      onChange?.(selectedDate);
      setOpen(false);
    }
  };

  const containerClasses = cn(
    "flex items-center rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm transition-colors",
    "focus-within:outline-none focus-within:ring-1 focus-within:ring-ring",
    !isValidInput && "border-destructive focus-within:ring-destructive",
    disabled && "cursor-not-allowed opacity-50",
    className,
  );

  const segmentClasses = (segment: "year" | "month" | "day") =>
    cn(
      "bg-transparent border-0 p-0 focus:outline-none text-center",
      segment === "year" ? "w-11" : "w-6",
      focusedSegment === segment && "bg-accent/20 rounded-sm",
      disabled && "cursor-not-allowed",
    );

  return (
    <div className={cn("relative", className)}>
      <div className={containerClasses}>
        <input
          ref={yearRef}
          type="text"
          value={year}
          onChange={handleYearChange}
          onKeyDown={(e) => handleKeyDown(e, "year")}
          onFocus={() => setFocusedSegment("year")}
          onBlur={handleBlur}
          placeholder="YYYY"
          disabled={disabled}
          className={segmentClasses("year")}
          maxLength={4}
        />
        <span className="mx-0.5 text-muted-foreground">/</span>
        <input
          ref={monthRef}
          type="text"
          value={month}
          onChange={handleMonthChange}
          onKeyDown={(e) => handleKeyDown(e, "month")}
          onFocus={() => setFocusedSegment("month")}
          onBlur={handleBlur}
          placeholder="MM"
          disabled={disabled}
          className={segmentClasses("month")}
          maxLength={2}
        />
        <span className="mx-0.5 text-muted-foreground">/</span>
        <input
          ref={dayRef}
          type="text"
          value={day}
          onChange={handleDayChange}
          onKeyDown={(e) => handleKeyDown(e, "day")}
          onFocus={() => setFocusedSegment("day")}
          onBlur={handleBlur}
          placeholder="DD"
          disabled={disabled}
          className={segmentClasses("day")}
          maxLength={2}
        />

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto h-7 w-7 p-0 hover:bg-transparent"
              disabled={disabled}
              tabIndex={-1}
            >
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="end">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleCalendarSelect}
              disabled={(day) => {
                if (minDate && day < minDate) return true;
                if (maxDate && day > maxDate) return true;
                return false;
              }}
              initialFocus
              defaultMonth={date}
              captionLayout="dropdown"
              startMonth={minDate}
              endMonth={calendarEndMonth}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Hidden input for form submission */}
      {name && (
        <input
          type="hidden"
          id={id}
          name={name}
          value={date ? format(date, "yyyy-MM-dd") : ""}
          required={required}
        />
      )}
    </div>
  );
}
